import { CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { HttpEventType } from '@angular/common/http';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { debounce, defer, delay, differenceWith, uniq, without } from 'lodash-es';
import { DateTime } from 'luxon';
import {
  catchError,
  finalize,
  fromEvent,
  last,
  map,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { v4 as uuid } from 'uuid';
import { AutofocusDirective } from '../../directive/autofocus.directive';
import { FakeLinkDirective } from '../../directive/fake-link.directive';
import { HasChanges } from '../../guard/pending-changes.guard';
import { Ref } from '../../model/ref';
import { EditorButton, sortOrder } from '../../model/tag';
import { mimeToCode } from '../../mods/media/code';
import { RelativePipe } from '../../pipe/relative.pipe';
import { AccountService } from '../../service/account.service';
import { AdminService } from '../../service/admin.service';
import { ProxyService } from '../../service/api/proxy.service';
import { RefService } from '../../service/api/ref.service';
import { StompService } from '../../service/api/stomp.service';
import { TaggingService } from '../../service/api/tagging.service';
import { AuthzService } from '../../service/authz.service';
import { ConfigService } from '../../service/config.service';
import { EditorService } from '../../service/editor.service';
import { Store } from '../../store/store';
import { readFileAsDataURL, readFileAsString } from '../../util/async';
import { URI_REGEX } from '../../util/format';
import { getArgs } from '../../util/query';
import { braces, hasTag, tagOrigin } from '../../util/tag';
import { LoadingComponent } from '../loading/loading.component';
import { ChatEntryComponent } from './chat-entry/chat-entry.component';

export interface ChatUpload {
  readonly id: string;
  readonly name: string;
  readonly progress: number;
  readonly completed?: boolean;
  readonly error?: string;
  readonly ref?: Ref | null;
}

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  host: { 'class': 'chat ext' },
  imports: [
    RelativePipe,
    FakeLinkDirective,
    ChatEntryComponent,
    LoadingComponent,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    ReactiveFormsModule,
    AutofocusDirective,
  ],
})
export class ChatComponent implements HasChanges {
  config = inject(ConfigService);
  private accounts = inject(AccountService);
  admin = inject(AdminService);
  private store = inject(Store);
  private auth = inject(AuthzService);
  private refs = inject(RefService);
  private editor = inject(EditorService);
  private stomp = inject(StompService);
  private proxy = inject(ProxyService);
  private ts = inject(TaggingService);

  private destroyRef = inject(DestroyRef);
  readonly itemSize = 18.5;
  private readonly windowHeight = toSignal(fromEvent(window, 'resize').pipe(
    map(() => window.innerHeight),
  ), { initialValue: window.innerHeight });

  readonly query = input('chat');
  readonly responseOf = input<Ref>();
  readonly responseUrl = computed(() => this.responseOf()?.url);

  readonly viewport = viewChild.required<CdkVirtualScrollViewport>('viewport');

  cursors = new Map<string, string | undefined>();
  readonly loadingPrev = linkedSignal(() => { this.query(); this.responseUrl(); return false; });
  readonly plugins = linkedSignal(() => this.store.account.defaultEditors(['plugin/latex']));
  readonly lastPoll = signal<DateTime>(DateTime.now());
  initialSize = 50;
  readonly messages = linkedSignal<Ref[] | undefined>(() => { this.query(); this.responseUrl(); return undefined; });
  readonly addText = linkedSignal(() => { this.query(); this.responseUrl(); return ''; });
  readonly sending = linkedSignal<Ref[]>(() => { this.query(); this.responseUrl(); return []; });
  readonly errored = linkedSignal<Ref[]>(() => { this.query(); this.responseUrl(); return []; });
  readonly scrollLock = linkedSignal<number | undefined>(() => { this.query(); this.responseUrl(); return undefined; });
  readonly notAtBottom = linkedSignal(() => { this.query(); this.responseUrl(); return false; });
  readonly uploads = linkedSignal<readonly ChatUpload[]>(() => { this.query(); this.responseUrl(); return []; });
  readonly dropping = linkedSignal(() => { this.query(); this.responseUrl(); return false; });
  readonly latex = linkedSignal(() => !!this.admin.getPlugin('plugin/latex'));
  readonly tags = linkedSignal(() => {
    this.query();
    this.responseUrl();
    return this.store.account.defaultEditors(this.editors());
  });

  private timeoutId?: number;
  private retries = 0;
  private lastScrolled = 0;
  private watch?: Subscription;
  private readonly uploadSubscriptions = new Map<string, Subscription>();
  private readonly queryChanged = new Subject<void>();

  constructor() {
    effect(() => {
      this.query();
      this.responseUrl();
      untracked(() => this.init());
    });
  }

  saveChanges() {
    //TODO:
    return true;
  }

  private readonly onDestroy = inject(DestroyRef).onDestroy(() => {
    this.clearPoll();
    this.refresh.cancel();
    this.cancelAllUploads();
  });

  init() {
    this.queryChanged.next();
    this.clearPoll();
    this.refresh.cancel();
    this.cancelAllUploads();
    this.cursors.clear();
    this.loadPrev(true);
    if (this.config.websockets) {
      this.watch?.unsubscribe();
      this.watch = this.stomp.watchTag(this.query()).pipe(
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(tag =>  this.refresh(tagOrigin(tag)));
    }
  }

  readonly editors = computed(() => this.editorButtons().map(p => p?.toggle as string).filter(p => !!p));

  readonly editorButtons = computed(() => {
    return sortOrder(this.admin.getEditorButtons()).reverse();
  });

  readonly editorPushButtons = computed(() => this.editorButtons().filter(b => !b.ribbon && this.visible(b)));

  visible(button: EditorButton) {
    if (button.scheme) return false;
    if (!button.global) return false;
    if (button.toggle && !this.auth.canAddTag(button.toggle)) return false;
    return true;
  }

  readonly containerHeight = computed(() => {
    return Math.max(300, Math.min(this.windowHeight() - 400, this.itemSize * (this.messages()?.length || 1)));
  });

  refresh = debounce((origin?: string) => {
    if (origin === undefined) {
      for (const origin of this.cursors.keys()) {
        this.loadMore(origin);
      }
    } else {
      this.loadMore(origin);
    }
  }, 400);

  loadMore(origin: string) {
    this.clearPoll();
    if (!this.cursors.has(origin!)) {
      this.loadPrev(true);
      return;
    }
    this.lastPoll.set(DateTime.now());
    const query = braces(this.query()) + ':' + (origin || '@');
    this.refs.page({
      ...getArgs(
        query,
        'modified,ASC',
        this.store.view.filter(),
        this.store.view.search(),
        this.store.view.pageNumber(),
        this.store.view.pageSize(),
      ),
      responses: this.responseOf()?.url,
      modifiedAfter: this.cursors.get(origin!)
    }).pipe(
      catchError(err => {
        this.setPoll(true);
        this.messages.update(messages => [...messages || []]);
        return throwError(() => err);
      }),
      takeUntilDestroyed(this.destroyRef),
      takeUntil(this.queryChanged),
    ).subscribe(page => {
      this.setPoll(!page.content.length);
      this.messages.update(messages => messages || []);
      if (!page.content.length) return;
      this.messages.update(messages => [...messages || [], ...page.content.filter(r => !hasTag('+plugin/placeholder', r))]);
      const last = page.content[page.content.length - 1];
      this.cursors.set(origin, last?.modifiedString);
      // TODO: verify read before clearing?
      this.accounts.clearNotificationsIfNone(last.modified, last.origin);
      this.sending.update(sending => differenceWith(sending, page.content, (a, b) => a.url === b.url));
      defer(() => this.viewport().checkViewportSize());
      if (!this.scrollLock()) this.scrollDown();
    });
  }

  loadPrev(scrollDown = false) {
    if (this.loadingPrev()) return;
    this.loadingPrev.set(true);
    this.lastPoll.set(DateTime.now());
    this.refs.page({
      ...getArgs(
        this.query(),
        'modified,DESC',
        this.store.view.filter(),
        this.store.view.search(),
        this.store.view.pageNumber(),
        Math.max(this.store.view.pageSize(), !this.cursors.size ? this.initialSize : 0),
      ),
      responses: this.responseOf()?.url,
      modifiedBefore: this.messages()?.[0]?.modifiedString,
    }).pipe(
      catchError(err => {
        this.loadingPrev.set(false);
        this.setPoll(true);
        this.messages.update(messages => [...messages || []]);
        return throwError(() => err);
      }),
      takeUntilDestroyed(this.destroyRef),
      takeUntil(this.queryChanged),
    ).subscribe(page => {
      this.loadingPrev.set(false);
      this.setPoll(!page.content.length);
      this.messages.update(messages => messages || []);
      this.scrollLock.set(undefined);
      if (!page.content.length) return;
      for (const ref of page.content) {
        if (!this.cursors.has(ref.origin!)) {
          this.cursors.set(ref.origin!, ref.modifiedString);
        }
      }
      this.messages.update(messages => [
        ...[...page.content].reverse().filter(r => !hasTag('+plugin/placeholder', r)),
        ...messages || [],
      ]);
      this.sending.update(sending => differenceWith(sending, page.content, (a, b) => a.url === b.url));
      defer(() => this.viewport().checkViewportSize());
      if (scrollDown) {
        this.retries = 0;
        this.scrollDown();
      } else {
        this.viewport().scrollToIndex(0, 'smooth');
      }
    });
  }

  scrollDown() {
    defer(() => {
      let wait = 0;
      if (this.lastScrolled < this.messages()!.length / 2) {
        this.lastScrolled = Math.floor((this.lastScrolled + this.messages()!.length) / 2);
        this.viewport().scrollToIndex(this.lastScrolled, 'smooth');
        wait += 400;
      }
      if (this.lastScrolled < this.messages()!.length - 1) {
        this.lastScrolled = this.messages()!.length - 1;
        delay(() => this.viewport().scrollToIndex(this.lastScrolled, 'smooth'), wait);
      }
    });
  }

  scrollToBottom() {
    this.scrollLock.set(undefined);
    this.viewport().scrollTo({ bottom: 0, behavior: 'smooth' })
    this.viewport().checkViewportSize();
    delay(() => this.viewport().scrollToIndex(this.messages()!.length - 1, 'smooth'), 400);
  }

  fetch() {
    if (!this.watch) {
      this.setPoll(false);
    }
  }

  clearPoll() {
    if (this.timeoutId !== undefined) {
      clearTimeout(this.timeoutId)
      this.timeoutId = undefined;
    }
  }

  setPoll(backoff: boolean) {
    this.clearPoll();
    if (this.watch) return;
    if (backoff) {
      this.retries++;
    } else {
      this.retries = 0;
    }
    this.timeoutId = window.setTimeout(() => this.refresh(),
      // 1 second to ~4 minutes in 10 steps
      Math.max(1000, 250 * Math.pow(2, Math.min(10, this.retries))));
  }

  add(text = '') {
    if (!text) {
      this.addText.update(text => text.trim());
      if (!this.addText()) return;
      text = this.addText();
      this.addText.set('');
    }
    this.scrollLock.set(undefined);
    const newTags = uniq([
      'internal',
      ...this.tags(),
      ...([this.store.view.localTag() || 'chat', ...this.store.view.ext()?.config?.addTags || []]),
      ...this.plugins(),
      ...(this.latex() ? ['plugin/latex'] : []),
      ...(this.store.account.localTag() ? [this.store.account.localTag()] : []),
    ]).filter(t => !!t);
    if (URI_REGEX.test(text)) {
      const url = this.editor.getRefUrl(text);
      newTags.push(...this.admin.getPluginsForExtension(url).filter(p => !newTags.includes(p.tag)));
      this.send({
        url,
        origin: this.store.account.origin(),
        tags: newTags,
      });
    } else {
      this.send({
        url: 'comment:' + uuid(),
        origin: this.store.account.origin(),
        comment: text,
        tags: newTags,
      });
    }
  }

  private send(ref: Ref) {
    const responseOf = this.responseOf();
    if (responseOf) ref = { ...ref, sources: [responseOf.url] };
    this.sending.update(sending => [...sending, ref]);
    (ref.modified ? this.refs.update(ref).pipe(
      map(() => ref),
      catchError(err => {
        if (err.status === 403) {
          // Ref already exists, repost
          this.sending.update(sending => without(sending, ref));
          ref = {
            ...ref,
            url: 'comment:' + uuid(),
            tags: [...ref.tags!, 'plugin/repost'],
            sources: [ ref.url, ...ref.sources || [] ],
          };
          this.sending.update(sending => [...sending, ref]);
          return this.refs.create(ref);
        } else if (err.status === 409) {
          // Ref already exists, repost
          return this.refs.get(ref.url, ref.origin).pipe(
            switchMap(r => this.refs.update({
              ...ref,
              modified: r.modified,
              modifiedString: r.modifiedString,
            })),
          );
        } else {
          this.sending.update(sending => without(sending, ref));
          this.errored.update(errored => [...errored, ref]);
        }
        return throwError(err);
      }),
    ) : this.refs.create(ref).pipe(
      map(() => ref),
      catchError(err => {
        if (err.status === 409) {
          // Ref already exists, repost
          this.sending.update(sending => without(sending, ref));
          ref = {
            ...ref,
            url: 'comment:' + uuid(),
            tags: [...ref.tags!, 'plugin/repost'],
            sources: [ ref.url, ...ref.sources || [] ],
          };
          this.sending.update(sending => [...sending, ref]);
          return this.refs.create(ref);
        } else {
          this.sending.update(sending => without(sending, ref));
          this.errored.update(errored => [...errored, ref]);
        }
        return throwError(err);
      }),
    )).pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.queryChanged)).subscribe(() => {
      this.fetch();
    });
  }

  retry(ref: Ref) {
    this.errored.update(errored => without(errored, ref));
    this.send(ref);
  }

  onScroll(index: number) {
    this.notAtBottom.set(this.viewport().measureScrollOffset('bottom') > this.itemSize);
    if (!this.scrollLock()) return;
    // TODO: count height in rows
    const diff = this.scrollLock()! - index;
    if (diff < -5) {
      this.scrollLock.set(undefined);
    }
  }

  toggleTag(button: EditorButton) {
    const tag = button.toggle!;
    if (this.buttonOn(tag)) {
      this.tags.update(tags => tags.filter(t => t !== tag));
      if (button.remember && this.admin.getTemplate('user')) {
        this.accounts.removeConfigArray$('editors', tag).subscribe();
      }
    } else {
      this.tags.update(tags => [...tags, tag]);
      if (button.remember && this.admin.getTemplate('user')) {
        this.accounts.addConfigArray$('editors', tag).subscribe();
      }
    }
    if ('vibrate' in navigator) navigator.vibrate([2, 8, 8]);
  }

  buttonOn(tag: string) {
    return this.tags()?.includes(tag);
  }

  handlePaste(event: ClipboardEvent) {
    const items = event.clipboardData?.items;
    if (!items) return;

    // First, check for files
    const files = [] as File[];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item?.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    // If files found and plugin/code or plugin/file is enabled, upload them
    if (files.length && (this.admin.getPlugin('plugin/code') || this.admin.getPlugin('plugin/file'))) {
      event.preventDefault();
      event.stopPropagation();
      this.upload(files);
      return;
    }
  }

  handleDrop(event: DragEvent) {
    this.dropping.set(false);
    const items = event.dataTransfer?.items;
    if (!items) return;

    // Check for files first
    const files = [] as File[];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item?.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    // If files found and plugin/code or plugin/file is enabled, upload them
    if (files.length && (this.admin.getPlugin('plugin/code') || this.admin.getPlugin('plugin/file'))) {
      event.preventDefault();
      event.stopPropagation();
      this.upload(files);
      return;
    }

    // Check for URL in text content
    const text = event.dataTransfer?.getData('text/plain')?.trim();
    if (!text) return;
    event.preventDefault();
    event.stopPropagation();
    this.addText.set(text);
  }

  dragLeave(event: DragEvent) {
    const target = event.target as HTMLElement;
    const relatedTarget = event.relatedTarget as HTMLElement;
    // Only set dropping to false if we're leaving the container entirely
    if (this.dropping() && (!relatedTarget || !target.contains(relatedTarget))) {
      this.dropping.set(false);
    }
  }

  upload(files: File[]) {
    if (!files || files.length === 0) return;
    files.forEach((file) => {
      const upload: ChatUpload = {
        id: uuid(),
        name: file.name,
        progress: 0
      };
      this.uploads.update(uploads => [...uploads, upload]);
      const subscription = this.upload$(file, upload).pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.uploadSubscriptions.delete(upload.id)),
      ).subscribe(ref => {
        const currentUpload = this.uploads().find(u => u.id === upload.id);
        if (ref && currentUpload && !currentUpload.error) {
          this.updateUpload(upload.id, { completed: true, progress: 100, ref });
          this.add(ref.url);
          this.uploads.update(uploads => uploads.filter(u => u.id !== upload.id));
        }
      });
      if (!subscription.closed) this.uploadSubscriptions.set(upload.id, subscription);
    });
  }

  updateUpload(id: string, patch: Partial<Omit<ChatUpload, 'id'>>) {
    this.uploads.update(uploads => uploads.map(upload => upload.id === id ? { ...upload, ...patch } : upload));
  }

  publicUploads() {
    return !!this.tags()?.includes('public') || !!this.store.view.ext()?.config?.addTags?.includes('public');
  }

  upload$(file: File, upload: ChatUpload): Observable<Ref | null> {
    const visibility = this.publicUploads() ? ['public'] : [];
    const codeType = this.admin.getPlugin('plugin/code') ? mimeToCode(file.type, file.name) : [];
    if (codeType.length) {
      const ref: Ref = {
        origin: this.store.account.origin(),
        url: 'internal:' + uuid(),
        // Upload as private unless the chat is public
        tags: uniq([
          ...visibility,
          this.store.account.localTag(),
          'internal',
          ...file.type === 'text/markdown' ? [] : codeType
        ])
      };
      this.updateUpload(upload.id, { progress: 50 });
      return readFileAsString(file).pipe(
        switchMap(contents => this.refs.create({
          ...ref,
          comment: contents,
        })),
        map(cursor => ({ ...ref, modifiedString: cursor, modified: DateTime.fromISO(cursor) })),
        tap(() => this.updateUpload(upload.id, { progress: 100 })),
        catchError(err => {
          this.updateUpload(upload.id, { error: err.message || $localize`Upload failed`, progress: 0 });
          return readFileAsDataURL(file).pipe(map(url => ({
            ...ref,
            url,
          } as Ref)));
        }),
      );
    } else if (this.admin.getPlugin('plugin/file')) {
      // Upload binary files as private unless the chat is public
      const tags: string[] = [...visibility, 'plugin/file'];
      if (file.type.startsWith('audio/') && this.admin.getPlugin('plugin/audio')) {
        tags.push('plugin/audio');
      } else if (file.type.startsWith('video/') && this.admin.getPlugin('plugin/video')) {
        tags.push('plugin/video', 'plugin/thumbnail');
      } else if (file.type.startsWith('image/') && this.admin.getPlugin('plugin/image')) {
        tags.push('plugin/image', 'plugin/thumbnail');
      } else if (file.type.startsWith('application/pdf') && this.admin.getPlugin('plugin/pdf')) {
        tags.push('plugin/pdf');
      }
      return this.proxy.save(file, this.store.account.origin()).pipe(
        map(event => {
          switch (event.type) {
            case HttpEventType.Response:
              return event.body;
            case HttpEventType.UploadProgress:
              const percentDone = event.total ? Math.round(100 * event.loaded / event.total) : 0;
              this.updateUpload(upload.id, { progress: percentDone });
              return null;
          }
          return null;
        }),
        last(),
        switchMap(ref => !ref ? of(ref) : this.ts.patch(tags, ref.url, ref.origin).pipe(
          map(cursor => ({
            ...ref,
            tags: uniq([...ref?.tags || [], ...tags]),
            modifiedString: cursor,
            modified: DateTime.fromISO(cursor),
          })),
        )),
        catchError(err => {
          this.updateUpload(upload.id, { error: err.message || $localize`Upload failed`, progress: 0 });
          return readFileAsDataURL(file).pipe(map(url => ({
            url,
            tags,
            origin: this.store.account.origin()
          } as Ref)));
        }),
      );
    } else {
      this.updateUpload(upload.id, { error: $localize`File uploads not enabled`, progress: 0 });
      return of(null);
    }
  }

  cancelUpload(upload: ChatUpload) {
    this.uploadSubscriptions.get(upload.id)?.unsubscribe();
    this.uploadSubscriptions.delete(upload.id);
    this.uploads.update(uploads => uploads.filter(u => u.id !== upload.id));
  }

  cancelAllUploads() {
    this.uploadSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.uploadSubscriptions.clear();
    this.uploads.set([]);
  }

  readonly hasActiveUploads = computed(() => this.uploads().some(u => !u.completed && !u.error));

}
